import React from 'react';
import LegalPageLayout, { Section, Paragraph, List, Highlight } from '../../components/LegalPageLayout';

const CommunityStandards = () => {
    return (
        <LegalPageLayout title="Community Standards" lastUpdated="March 2026">
            <Section title="Our Mission">
                <Paragraph>
                    ClientCheck exists to help contractors make better business decisions through 
                    shared professional experiences. We believe that transparent, factual information 
                    benefits the entire contractor community.
                </Paragraph>
                <Paragraph>
                    Our community standards reflect our commitment to maintaining a platform that is 
                    professional, fair, and useful for everyone.
                </Paragraph>
            </Section>

            <Section title="Core Principles">
                <Paragraph>
                    The ClientCheck community is built on these core principles:
                </Paragraph>
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Professionalism</h3>
                <Paragraph>
                    We maintain a professional environment where contractors can share job experiences 
                    without personal attacks, inappropriate language, or unprofessional conduct. 
                    All interactions should reflect the standards of a business setting.
                </Paragraph>
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Fairness</h3>
                <Paragraph>
                    We strive for fairness in how reviews are handled and how experience summaries are calculated. 
                    Our systems account for context, recognize that circumstances vary, and avoid 
                    penalizing addresses unfairly.
                </Paragraph>
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Accuracy</h3>
                <Paragraph>
                    We value accurate, truthful information. Reviews should reflect genuine experiences, 
                    and users should only submit reviews for jobs they actually worked on.
                </Paragraph>
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Respect</h3>
                <Paragraph>
                    We expect users to respect each other and the broader community. This includes 
                    respecting privacy, avoiding personal attacks, and using the platform as intended.
                </Paragraph>
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Responsibility</h3>
                <Paragraph>
                    Users are responsible for the content they submit. We expect honesty, 
                    accountability, and thoughtful use of the platform.
                </Paragraph>
            </Section>

            <Section title="What We Encourage">
                <List items={[
                    "Sharing factual job experiences that help other contractors",
                    "Using structured ratings to provide consistent, useful data",
                    "Adding context that explains circumstances fairly",
                    "Writing reviews that focus on job-related matters",
                    "Reporting content that violates community standards",
                    "Engaging with the platform in good faith"
                ]} />
            </Section>

            <Section title="What We Discourage">
                <List items={[
                    "Personal vendettas or using reviews for retaliation",
                    "Exaggerated or inflammatory language",
                    "Reviews based on hearsay rather than personal experience",
                    "Attempts to manipulate scores or game the system",
                    "Using the platform to harm competitors"
                ]} />
            </Section>

            <Section title="Supporting Better Decisions">
                <Paragraph>
                    ClientCheck is designed to support better business decisions—not to enable 
                    harassment or retaliation. When used properly, the platform helps contractors:
                </Paragraph>
                <List items={[
                    "Identify potential payment concerns before accepting work",
                    "Understand communication patterns at specific addresses",
                    "Make informed decisions about job opportunities",
                    "Build a track record of reliable, trustworthy reporting"
                ]} />
                <Highlight>
                    Our goal is a contractor community that benefits from shared knowledge 
                    while maintaining professional standards and mutual respect.
                </Highlight>
            </Section>

            <Section title="Building Trust">
                <Paragraph>
                    Trust is earned through consistent, responsible platform use. Contractors who 
                    submit accurate, professional reviews build higher trust levels over time, 
                    which gives their reviews more weight in our scoring system.
                </Paragraph>
                <Paragraph>
                    We encourage all users to think of their reviews as professional documentation 
                    that will be seen by their peers in the contractor community.
                </Paragraph>
            </Section>

            <Section title="Reporting Concerns">
                <Paragraph>
                    If you see content that violates our community standards, please report it. 
                    Our moderation team reviews all reports and takes appropriate action to 
                    maintain platform integrity.
                </Paragraph>
            </Section>
        </LegalPageLayout>
    );
};

export default CommunityStandards;
